"use client";
import Image from "next/image";
import { aboutUs } from "@assets";
import { motion } from "framer-motion";

const AboutPage = () => {
	return (
		<main className="w-full">
			<section className="from-purple-20 to-blue-20 xs:py-10 relative w-full overflow-hidden bg-linear-to-br py-8 sm:py-12 md:py-16 lg:py-20 xl:py-24">
				<div className="absolute inset-0 bg-black/10"></div>
				<motion.div initial={{ opacity: 0, scale: 1.1 }} animate={{ opacity: 0.4, scale: 1 }} transition={{ duration: 1.2, ease: "easeOut" }} className="absolute top-0 right-0 h-full w-full">
					<Image src={aboutUs} alt="About Us Background" className="h-full w-full object-cover" priority />
				</motion.div>
				<div className="xs:px-4 relative z-10 mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 xl:px-12">
					<motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: "easeOut" }} className="text-center">
						<motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }} className="xs:text-3xl xs:mb-4 mb-3 text-2xl font-bold text-gray-900 sm:mb-6 sm:text-4xl md:text-5xl lg:mb-8 lg:text-6xl xl:text-6xl">
							About Cognition
						</motion.h1>
						<motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }} className="xs:text-base xs:max-w-3xl mx-auto max-w-2xl text-sm text-gray-600 sm:text-lg md:text-xl lg:text-2xl">
							Empowering learners worldwide with quality education and skill development through innovative learning solutions.
						</motion.p>
					</motion.div>
				</div>
			</section>
			<section className="xs:py-8 py-6 sm:py-10 md:py-12 lg:py-16 xl:py-20">
				<div className="xs:px-4 mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 xl:px-12">
					<div className="xs:gap-8 grid grid-cols-1 items-center gap-6 sm:gap-10 md:gap-12 lg:grid-cols-2 lg:gap-16 xl:gap-20">
						<motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, ease: "easeOut" }} viewport={{ once: true }}>
							<motion.h2
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
								viewport={{ once: true }}
								className="xs:text-2xl xs:mb-4 mb-3 text-xl font-bold text-gray-900 sm:mb-6 sm:text-3xl md:text-4xl lg:mb-8 lg:text-3xl xl:text-4xl"
							>
								Our Mission
							</motion.h2>
							<motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }} viewport={{ once: true }} className="xs:text-base xs:mb-4 mb-3 text-sm text-gray-600 sm:mb-6 sm:text-lg md:text-xl lg:mb-8 lg:text-base xl:text-lg">
								To democratize education by providing accessible, high-quality learning experiences that empower individuals to achieve their full potential.
							</motion.p>
							<motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }} viewport={{ once: true }} className="xs:text-base text-sm text-gray-600 sm:text-lg md:text-xl lg:text-base xl:text-lg">
								We believe that education should be available to everyone, regardless of their background or circumstances. Our platform connects learners with expert instructors and comprehensive content designed for real-world success.
							</motion.p>
						</motion.div>
						<motion.div
							initial={{ opacity: 0, x: 50 }}
							whileInView={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.8, ease: "easeOut" }}
							viewport={{ once: true }}
							className="xs:rounded-xl xs:p-6 rounded-lg bg-linear-to-br from-purple-100 to-blue-100 p-4 sm:rounded-2xl sm:p-8 md:p-10 lg:rounded-3xl lg:p-12 xl:p-16"
						>
							<motion.div initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }} viewport={{ once: true }} className="text-center">
								<motion.div
									initial={{ opacity: 0, y: 20 }}
									whileInView={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
									viewport={{ once: true }}
									className="xs:text-5xl xs:mb-3 mb-2 text-4xl font-bold text-purple-600 sm:mb-4 sm:text-6xl md:text-7xl lg:mb-6 lg:text-6xl xl:text-7xl"
								>
									10K+
								</motion.div>
								<motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.6, ease: "easeOut" }} viewport={{ once: true }} className="xs:text-base text-sm text-gray-700 sm:text-lg md:text-xl lg:text-lg xl:text-xl">
									Active Learners
								</motion.div>
							</motion.div>
						</motion.div>
					</div>
				</div>
			</section>
			<section className="xs:py-8 bg-gray-50 py-6 sm:py-10 md:py-12 lg:py-16 xl:py-20">
				<div className="xs:px-4 mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 xl:px-12">
					<motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: "easeOut" }} viewport={{ once: true }} className="xs:mb-10 mb-8 text-center sm:mb-12 lg:mb-16">
						<motion.h2
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
							viewport={{ once: true }}
							className="xs:text-2xl xs:mb-3 mb-2 text-xl font-bold text-gray-900 sm:mb-4 sm:text-3xl md:text-4xl lg:mb-6 lg:text-3xl xl:text-4xl"
						>
							Our Values
						</motion.h2>
						<motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }} viewport={{ once: true }} className="xs:text-base text-sm text-gray-600 sm:text-lg md:text-xl lg:text-base xl:text-lg">
							The principles that guide everything we do
						</motion.p>
					</motion.div>
					<div className="xs:gap-6 grid grid-cols-1 gap-4 sm:gap-8 md:grid-cols-3 lg:gap-10">
						<motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }} viewport={{ once: true }} whileHover={{ y: -10, scale: 1.05 }} className="text-center">
							<motion.div
								initial={{ opacity: 0, scale: 0.8 }}
								whileInView={{ opacity: 1, scale: 1 }}
								viewport={{ once: true }}
								whileHover={{ rotate: 360 }}
								transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
								className="xs:w-14 xs:h-14 xs:mb-4 mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 sm:mb-5 sm:h-16 sm:w-16 md:h-18 md:w-18 lg:mb-6 lg:h-20 lg:w-20 xl:h-24 xl:w-24"
							>
								<svg className="xs:w-7 xs:h-7 h-6 w-6 text-purple-600 sm:h-8 sm:w-8 md:h-9 md:w-9 lg:h-10 lg:w-10 xl:h-12 xl:w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
									/>
								</svg>
							</motion.div>
							<motion.h3
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.6, ease: "easeOut" }}
								viewport={{ once: true }}
								className="xs:text-lg xs:mb-2 mb-1 text-base font-semibold text-gray-900 sm:mb-3 sm:text-xl md:text-2xl lg:mb-4 lg:text-xl xl:text-2xl"
							>
								Quality Education
							</motion.h3>
							<motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.8, ease: "easeOut" }} viewport={{ once: true }} className="xs:text-sm text-xs text-gray-600 sm:text-base md:text-lg lg:text-sm xl:text-base">
								We maintain the highest standards in our course content and delivery methods.
							</motion.p>
						</motion.div>

						<motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }} viewport={{ once: true }} whileHover={{ y: -10, scale: 1.05 }} className="text-center">
							<motion.div
								initial={{ opacity: 0, scale: 0.8 }}
								whileInView={{ opacity: 1, scale: 1 }}
								viewport={{ once: true }}
								whileHover={{ rotate: 360 }}
								transition={{ duration: 0.6, delay: 0.6, ease: "easeOut" }}
								className="xs:w-14 xs:h-14 xs:mb-4 mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 sm:mb-5 sm:h-16 sm:w-16 md:h-18 md:w-18 lg:mb-6 lg:h-20 lg:w-20 xl:h-24 xl:w-24"
							>
								<svg className="xs:w-7 xs:h-7 h-6 w-6 text-blue-600 sm:h-8 sm:w-8 md:h-9 md:w-9 lg:h-10 lg:w-10 xl:h-12 xl:w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
									/>
								</svg>
							</motion.div>
							<motion.h3
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.8, ease: "easeOut" }}
								viewport={{ once: true }}
								className="xs:text-lg xs:mb-2 mb-1 text-base font-semibold text-gray-900 sm:mb-3 sm:text-xl md:text-2xl lg:mb-4 lg:text-xl xl:text-2xl"
							>
								Inclusivity
							</motion.h3>
							<motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 1.0, ease: "easeOut" }} viewport={{ once: true }} className="xs:text-sm text-xs text-gray-600 sm:text-base md:text-lg lg:text-sm xl:text-base">
								Education should be accessible to everyone, regardless of background or location.
							</motion.p>
						</motion.div>

						<motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.6, ease: "easeOut" }} viewport={{ once: true }} whileHover={{ y: -10, scale: 1.05 }} className="text-center">
							<motion.div
								initial={{ opacity: 0, scale: 0.8 }}
								whileInView={{ opacity: 1, scale: 1 }}
								viewport={{ once: true }}
								whileHover={{ rotate: 360 }}
								transition={{ duration: 0.6, delay: 0.8, ease: "easeOut" }}
								className="xs:w-14 xs:h-14 xs:mb-4 mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 sm:mb-5 sm:h-16 sm:w-16 md:h-18 md:w-18 lg:mb-6 lg:h-20 lg:w-20 xl:h-24 xl:w-24"
							>
								<svg className="xs:w-7 xs:h-7 h-6 w-6 text-green-600 sm:h-8 sm:w-8 md:h-9 md:w-9 lg:h-10 lg:w-10 xl:h-12 xl:w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
								</svg>
							</motion.div>
							<motion.h3
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 1.0, ease: "easeOut" }}
								viewport={{ once: true }}
								className="xs:text-lg xs:mb-2 mb-1 text-base font-semibold text-gray-900 sm:mb-3 sm:text-xl md:text-2xl lg:mb-4 lg:text-xl xl:text-2xl"
							>
								Innovation
							</motion.h3>
							<motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 1.2, ease: "easeOut" }} viewport={{ once: true }} className="xs:text-sm text-xs text-gray-600 sm:text-base md:text-lg lg:text-sm xl:text-base">
								We continuously evolve our platform to provide the best learning experience.
							</motion.p>
						</motion.div>
					</div>
				</div>
			</section>
		</main>
	);
};

export default AboutPage;
